import { createContext, ReactNode, useContext, useMemo } from 'react'

import { EventProperties } from 'types/analytics'

interface AnalyticsSectionInterface {
  // The nearest section's name. Kept for the Amplitude `useAnalytics` hook, whose
  // event names use a single section segment — unchanged by nesting.
  name: string
  properties: EventProperties
  // The full nested section path (root -> leaf) and the properties merged down the
  // chain. Read by the PostHog `useTrackEvent` hook to build hierarchical names
  // like "SMS Input > Coach Prompt > ...". Amplitude does not read these, so
  // nesting never alters existing Amplitude events.
  path: string[]
  mergedProperties: EventProperties
}

const AnalyticsSectionContext = createContext<AnalyticsSectionInterface | null>(
  null
)

export const useAnalyticsSection = () => useContext(AnalyticsSectionContext)

interface Props {
  name: string
  properties?: EventProperties
  children: ReactNode
}

export const AnalyticsSection = ({
  name,
  properties = {},
  children,
}: Props) => {
  const parent = useContext(AnalyticsSectionContext)

  const contextValue = useMemo(
    () => ({
      name,
      properties,
      path: [...(parent?.path ?? []), name],
      mergedProperties: { ...(parent?.mergedProperties ?? {}), ...properties },
    }),
    [parent, name, properties]
  )

  return (
    <AnalyticsSectionContext.Provider value={contextValue}>
      {children}
    </AnalyticsSectionContext.Provider>
  )
}
